import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../../../components/PortfolioPerformance/page-header";
import { Panel } from "../../../components/PortfolioPerformance/panel";
import { Skeleton } from "../../../ui/skeleton";
import { CohortDevelopmentChart } from "../../../components/PortfolioPerformance/charts/cohort-development-chart";
import { BasisToggle, type ThreeWayBasis } from "../../../components/PortfolioPerformance/basis-toggle";
import { YearChips } from "../../../components/PortfolioPerformance/chart-filters";
import { useExpectedLoss, usePdCurve } from "../lib/api/expected-loss";

type PdRow = { month: string; [key: string]: number | string | null };

/** PD -- Actual vs. Projected, with a Realized / Projected / Active toggle: the toggle picks the rows of the chart table
 *  (realized_tf / cohort_active_tf); within them each point is the actual reading (solid) or the projection (dotted). */
function PdCurvePanel({ riskAppetitePct }: { riskAppetitePct: number }) {
  const [basis, setBasis] = useState<ThreeWayBasis>("realized");
  // Funded-year chips, same as the Loss Development chart's: empty = every year shown.
  const [selected, setSelected] = useState<string[]>([]);
  const { data, isPending, isError } = usePdCurve(basis);

  const years = useMemo(() => Object.keys(data?.cohortKind ?? {}).sort(), [data]);
  const activeSelected = selected.filter((y) => years.includes(y));
  const visibleYears = activeSelected.length ? activeSelected : years;

  function toggleYear(year: string) {
    setSelected((prev) => (prev.includes(year) ? prev.filter((y) => y !== year) : [...prev, year]));
  }

  return (
    <Panel
      className="mt-6"
      title="PD — Actual vs. Projected"
      description="Realized readings solid, the projection after them dotted."
      right={
        <BasisToggle
          value={basis}
          onChange={setBasis}
          realizedHint="Contracts that have matured"
          projectedHint="Contracts still running"
          activeHint="Funded years that still have active contracts"
        />
      }
    >
      {isError ? (
        <p role="alert" className="text-sm text-destructive">
          Could not load this chart.
        </p>
      ) : isPending || !data ? (
        <Skeleton className="h-64 w-full" />
      ) : years.length === 0 ? (
        <p className="text-sm text-muted-foreground">No funded years under this toggle for this ticket size yet.</p>
      ) : (
        <>
          <YearChips years={years} selected={activeSelected} onToggle={toggleYear} />
          <CohortDevelopmentChart
            rows={data.pdCurve as PdRow[]}
            realized={data.realized ?? {}}
            years={visibleYears}
            allYears={years}
            keyPrefix="pd"
            xTitle="Month"
            xAxisLabel="Months since origination"
            height={300}
            referenceLine={{
              value: riskAppetitePct,
              label: `Risk appetite ${riskAppetitePct}%`,
            }}
          />
        </>
      )}
    </Panel>
  );
}

export default function ExpectedLossPage() {
  const { data, isPending, isError, error } = useExpectedLoss();

  return (
    <div>
      <PageHeader
        title="Expected Loss (PD · LGD · EAD)"
        subtitle="Actual PD by month and year, projected PD for immature vintages rendered as a distinct dotted curve, and the risk-appetite line."
      />

      {isError ? (
        <div
          role="alert"
          className="mt-8 border border-decline/50 bg-decline/10 p-5 text-sm text-foreground"
        >
          <p className="font-semibold">Could not load expected loss data.</p>
          <p className="mt-1 text-muted-foreground">
            {error instanceof Error ? error.message : "Unknown failure."}
          </p>
        </div>
      ) : isPending || !data ? (
        <div className="mt-8 space-y-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : (
        <>
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="border border-border bg-card p-5">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Projected Expected Loss ($) — {data.asOf}
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums text-foreground">
                {data.expectedLossDollars}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">Lifetime expected loss, owned book</p>
            </div>
            <div className="border border-border bg-card p-5">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Projected Probability of Default (PD)
              </p>
              {data.probabilityOfDefault != null ? (
                <p className="mt-1 text-2xl font-semibold tabular-nums text-foreground">
                  {data.probabilityOfDefault}%
                </p>
              ) : (
                <p className="mt-1 text-sm text-muted-foreground">Not Available Yet.</p>
              )}
              <p className="mt-1 text-xs text-muted-foreground">
                Probability of Default
              </p>
            </div>
            {data.lgdBackbone != null ? (
              <Link
                to="/portfolio-performance/lgd"
                className="border border-border bg-card p-5 transition-colors hover:border-primary/60 hover:bg-secondary/60"
              >
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Projected LGD (backbone)
                </p>
                <p className="mt-1 text-2xl font-semibold tabular-nums text-foreground">
                  {data.lgdBackbone}%
                </p>
                <p className="mt-1 text-xs text-muted-foreground">Net loss ÷ gross loss · click for detail</p>
              </Link>
            ) : (
              <div className="border border-border bg-card p-5">
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Projected LGD (backbone)
                </p>
                <p className="mt-1 text-sm text-muted-foreground">Not Available Yet.</p>
              </div>
            )}
            <div className="border border-border bg-card p-5">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Exposure at Default
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums text-foreground">
                {data.exposureAtDefault}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">Owned net investment, latest file date</p>
            </div>
          </div>

          <PdCurvePanel riskAppetitePct={data.riskAppetitePct} />
        </>
      )}
    </div>
  );
}
